// Third Party
import * as React from 'react';

// Utils
import { routes } from '@routes';
import { useLoginAndSignupModals } from '@contexts/use-login-and-signup-modals';
import { useScript, appleAuthHelpers } from 'react-apple-signin-auth';
// import getConfig from '@config';

// UI
import { Button } from '@components/button';
import { Link } from '@components/link';
import { Typography } from '@components/typography';
import { Input } from '@components/form/input';
import { PasswordInput } from '@components/form/password-input';
import { signIn } from 'next-auth/react';
import {
  SocialLogin,
  SocialLoginType,
} from '@components/social-login/social-login';

type Props = { message?: string; redirectUrl?: string };

export const Login: React.FC<Props> = ({ message: _message, redirectUrl }) => {
  // const {
  //   publicRuntimeConfig: {
  //     apple: { clientId: appleClientId },
  //     canonicalUrl,
  //   },
  // } = getConfig();

  const [message, setMessage] = React.useState(_message);
  const [isSubmitting, setSubmitting] = React.useState<boolean>(false);
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');

  const { deactivateLoginModal } = useLoginAndSignupModals();

  const onEmailChangeHandler = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(value);
  };

  const onPasswordChangeHandler = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(value);
  };

  useScript(appleAuthHelpers.APPLE_SCRIPT_SRC);

  return (
    <div className="mx-4 flex flex-col items-center justify-center pb-6 pt-2 sm:mx-0">
      <div className="flex flex-col text-center sm:mx-auto sm:w-full">
        <Typography
          component="h1"
          variant="title2"
          variantM="title1"
          className="text-primary-main"
        >
          Log in to your account
        </Typography>

        <Typography component="p" variant="body1" className="mt-1 text-center">
          <span>
            Or{' '}
            <Link
              to={routes.signup()}
              onClick={deactivateLoginModal}
              className="text-primary-light hover:text-secondary-dark"
            >
              create one today!
            </Link>
          </span>
        </Typography>
      </div>

      <form
        className="mx-4 mt-8 w-full max-w-md space-y-5 sm:mx-0"
        onSubmit={async (event) => {
          setSubmitting(true);
          event.preventDefault();

          const user = await signIn('credentials', {
            email,
            password,
            redirect: false,
            callbackUrl: redirectUrl,
          });

          if (user?.error) {
            setMessage('Incorrect email or password');
          } else {
            deactivateLoginModal();
            if (redirectUrl) {
              window.location.href = redirectUrl;
            }
          }

          setSubmitting(false);
        }}
      >
        {message && (
          <div className="mb-4 rounded-lg border-2 border-error-main bg-error-light p-3 text-center font-semibold text-error-main shadow-lg">
            {message}
          </div>
        )}

        <div className="w-full space-y-4">
          <Input
            id="email"
            name="email"
            label="Email"
            autoComplete="email"
            value={email}
            onChange={onEmailChangeHandler}
            required
          />

          <PasswordInput
            id="password"
            name="password"
            label="Password"
            autoComplete="current-password"
            value={password}
            onChange={onPasswordChangeHandler}
            required
          />
        </div>

        <div className="flex items-center justify-between">
          <Link
            to={routes.passwordForgot}
            onClick={deactivateLoginModal}
            className="-mt-1 mb-3 text-sm text-primary-light hover:text-secondary-dark"
          >
            Forgot your password?
          </Link>
        </div>

        <div className="mt-6">
          <Button
            isFullWidth={true}
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
            className="text-base hover:bg-secondary-main hover:text-primary-main"
          >
            Continue
          </Button>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-light" />
              </div>
              <div className="relative flex justify-center text-sm">
                <Typography
                  component="span"
                  variant="body2"
                  className="bg-white px-4"
                >
                  or
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </form>

      <div className="mx-auto mt-8 flex w-full max-w-md flex-col items-center space-y-4">
        <SocialLogin
          social={SocialLoginType.Google}
          onClick={() => signIn('google')}
          isSignUp={false}
        />

        {/* <SocialLogin
          social={SocialLoginType.Apple}
          onClick={() =>
            appleAuthHelpers.signIn({
              authOptions: {
                clientId: appleClientId,
                scope: 'email name',
                redirectURI: `${canonicalUrl.toString()}login/`,
                state: 'state',
                nonce: 'nonce',
                usePopup: true,
              },
              onSuccess: async (response: any) => {
                if (response?.authorization?.id_token) {
                  await signIn('AppleToken', {
                    token: response.authorization.id_token,
                    email: response.user?.email || '',
                    firstName: response.user?.name?.firstName || '',
                    lastName: response.user?.name?.lastName,
                  });
                } else {
                  setMessage('Apple login failed.');
                }
              },
              onError: (error: any) =>
                console.error('Apple login failed.', error),
            })
          }
        /> */}

        <SocialLogin
          social={SocialLoginType.Facebook}
          onClick={() => signIn('facebook')}
          isSignUp={false}
        />
      </div>
    </div>
  );
};
